import React from 'react';
import { DraftNumberInput } from '../../../../shared/components/DraftNumberInput';
import {
  DIFFICULTY_MULTIPLIERS,
  MATERIAL_COST_DEFAULTS,
  NRE_DEFAULTS,
  PERCENT_PER_FRACTION,
} from './materialCostModel';

/**
 * Slide-over drawer that surfaces every Material Traceability cost assumption
 * from the active Configuration and lets a rep override each one for this
 * proposal. Overrides are written to module.overrides: per-pipe and fee keys go
 * in flat, onboarding (NRE) keys carry the `nre.` prefix so they do not collide
 * with the per-pipe block (both have a `marginTarget`). Values are stored in the
 * backend-native units (fractions for percent fields); the drawer converts only
 * for display.
 */

const NRE_OVERRIDE_PREFIX = 'nre.';

// Fallback fees and floor, used only when the active config has not loaded yet.
const FEE_FALLBACKS: Record<string, number> = {
  gasAddOnFee: 2000,
  wireAddOnFee: 2000,
  coatingMaterialsAddOnFee: 3000,
  floorPerPipe: 0.5,
  ceilingPerPipe: 0,
  moduleFloor: 0,
};

// 'percent' fields store a fraction (0.75) but show a percent (75). 'discount'
// fields store the percent number itself (the backend discount is 10 for ten
// percent), so they display and store the same value.
type Unit = 'currency' | 'percent' | 'hours' | 'number' | 'discount';
// 'material' keys are flat overrides; 'nre' keys carry the nre. prefix (and may
// be dotted, e.g. difficultyMultipliers.api); 'flat' keys are written verbatim.
type Block = 'material' | 'nre' | 'flat';

interface FieldDef {
  key: string;
  label: string;
  unit: Unit;
  step?: number;
  tooltip?: string;
}

interface GroupDef {
  title: string;
  block: Block;
  note?: string;
  fields: FieldDef[];
}

const GROUPS: GroupDef[] = [
  {
    title: 'Per-pipe cost rates',
    block: 'material',
    fields: [
      { key: 'hourlyRate', label: 'Exception labour $/hr', unit: 'currency', tooltip: 'Offshore hourly rate for clearing extraction exceptions.' },
      { key: 'computeCostPerDoc', label: 'Compute $/doc', unit: 'currency', step: 0.01, tooltip: 'Auto-extraction compute cost per document.' },
      { key: 'storagePerPipe', label: 'Storage $/pipe', unit: 'currency', step: 0.01, tooltip: 'Flat storage cost carried per pipe.' },
      { key: 'qaOverheadPct', label: 'QA overhead %', unit: 'percent', tooltip: 'QA overhead added on top of exception labour.' },
      { key: 'marginTarget', label: 'Per-pipe margin %', unit: 'percent', tooltip: 'Target margin on the per-pipe charge: price = cost / (1 - margin).' },
    ],
  },
  {
    title: 'Per-pipe bounds',
    block: 'material',
    fields: [
      { key: 'floorPerPipe', label: 'Floor $/pipe', unit: 'currency', step: 0.1, tooltip: 'Minimum price per pipe.' },
      { key: 'ceilingPerPipe', label: 'Ceiling $/pipe', unit: 'currency', step: 0.5, tooltip: 'Maximum price per pipe. 0 means no ceiling.' },
    ],
  },
  {
    title: 'Per-pipe volume defaults',
    block: 'material',
    note: 'Mills can override these per row in the table above; these are the fallback when a mill leaves the column blank.',
    fields: [
      { key: 'reportsPerPipe', label: 'Reports / pipe', unit: 'number', tooltip: 'Documents per pipe used to size extraction and exceptions.' },
      { key: 'autoExtractRate', label: 'Auto-extract %', unit: 'percent', tooltip: 'Share auto-extracted; the remainder become manual exceptions.' },
      { key: 'minutesPerException', label: 'Min / exception', unit: 'number', tooltip: 'Minutes of labour to clear one extraction exception.' },
    ],
  },
  {
    title: 'Onboarding (NRE) rates',
    block: 'nre',
    note: 'Build hours = reports/pipe x hours/report type x format difficulty.',
    fields: [
      { key: 'setupHours', label: 'Setup hours', unit: 'hours', tooltip: 'Base engineering hours to onboard a new source.' },
      { key: 'perUnitHours', label: 'Hours / report type', unit: 'hours', tooltip: 'Build hours per report type, scaled by reports/pipe and format difficulty.' },
      { key: 'validationOverhead', label: 'Validation overhead %', unit: 'percent', tooltip: 'Validation overhead added to build hours.' },
      { key: 'engineerRate', label: 'Engineer $/hr', unit: 'currency', tooltip: 'Onboarding engineer hourly rate.' },
      { key: 'marginTarget', label: 'NRE margin %', unit: 'percent', tooltip: 'Target margin on onboarding: price = cost / (1 - margin).' },
    ],
  },
  {
    title: 'Format difficulty multipliers',
    block: 'nre',
    note: 'Higher difficulty means more onboarding build hours for that source format.',
    fields: [
      { key: 'difficultyMultipliers.api', label: 'API', unit: 'number', step: 0.1, tooltip: 'Build-hours multiplier for API / native-digital sources.' },
      { key: 'difficultyMultipliers.spreadsheet', label: 'Spreadsheet / CSV', unit: 'number', step: 0.1, tooltip: 'Build-hours multiplier for spreadsheet sources.' },
      { key: 'difficultyMultipliers.scannedPdf', label: 'Scanned PDF', unit: 'number', step: 0.1, tooltip: 'Build-hours multiplier for scanned-PDF sources.' },
      { key: 'difficultyMultipliers.handwritten', label: 'Handwritten', unit: 'number', step: 0.1, tooltip: 'Build-hours multiplier for handwritten sources.' },
    ],
  },
  {
    title: 'Discount',
    block: 'flat',
    note: 'Overrides the duration-band discount for this proposal. Applies to the per-pipe charge and the platform fee.',
    fields: [
      { key: 'durationDiscountPct', label: 'Duration discount %', unit: 'discount', tooltip: 'Leave at the band default, or set a discount percent to apply for this proposal.' },
    ],
  },
  {
    title: 'Add-on fees and floor',
    block: 'material',
    fields: [
      { key: 'gasAddOnFee', label: 'Gas add-on fee', unit: 'currency', tooltip: 'Flat fee when gas certificates are tracked.' },
      { key: 'wireAddOnFee', label: 'Wire add-on fee', unit: 'currency', tooltip: 'Flat fee when welding-wire certificates are tracked.' },
      { key: 'coatingMaterialsAddOnFee', label: 'Coating-materials fee', unit: 'currency', tooltip: 'Flat fee when coating-material records are tracked.' },
      { key: 'moduleFloor', label: 'Module floor', unit: 'currency', tooltip: 'Minimum the module charges if the summed lines fall below it.' },
    ],
  },
];

const overrideKeyFor = (block: Block, key: string): string =>
  block === 'nre' ? `${NRE_OVERRIDE_PREFIX}${key}` : key;

const toDisplay = (unit: Unit, value: number): number =>
  unit === 'percent' ? value * PERCENT_PER_FRACTION : value;

const fromDisplay = (unit: Unit, raw: string): number | undefined => {
  if (raw === '') return undefined;
  const n = Number(raw);
  if (Number.isNaN(n)) return undefined;
  return unit === 'percent' ? n / PERCENT_PER_FRACTION : n;
};

// Read a possibly-dotted key (e.g. difficultyMultipliers.api) out of a config map.
const readNested = (source: Record<string, unknown>, key: string): number | undefined => {
  const value = key
    .split('.')
    .reduce<unknown>((acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined), source);
  return typeof value === 'number' ? value : undefined;
};

interface Props {
  open: boolean;
  onClose: () => void;
  material: Record<string, number>;
  // The nre config holds a nested difficultyMultipliers map, so it is not a flat
  // number record; nested keys are read with readNested.
  nre: Record<string, unknown>;
  overrides: Record<string, number>;
  onOverrideChange: (key: string, value: number | undefined) => void;
  onResetAll: () => void;
  // The duration-band discount percent currently in effect (from the last
  // Calculate Price), shown as the default for the discount override field.
  discountDefault?: number;
}

const FieldRow: React.FC<{
  field: FieldDef;
  block: Block;
  defaultValue: number;
  override: number | undefined;
  onChange: (value: number | undefined) => void;
}> = ({ field, block, defaultValue, override, onChange }) => {
  const isOverridden = override !== undefined;
  const native = isOverridden ? override : defaultValue;
  return (
    <div>
      <DraftNumberInput
        id={`cost-${overrideKeyFor(block, field.key)}`}
        label={field.label}
        tooltip={field.tooltip}
        step={field.step ?? 'any'}
        value={toDisplay(field.unit, native)}
        className={isOverridden ? 'border-primary' : ''}
        onValueChange={(raw) => onChange(fromDisplay(field.unit, raw))}
      />
      <div className="mt-0.5 flex items-center justify-between text-[11px] text-heritage-grey">
        <span>default {toDisplay(field.unit, defaultValue)}{field.unit === 'percent' ? '%' : ''}</span>
        {isOverridden && (
          <button type="button" className="text-primary hover:underline" onClick={() => onChange(undefined)}>
            reset
          </button>
        )}
      </div>
    </div>
  );
};

export const MaterialCostAssumptionsDrawer: React.FC<Props> = ({
  open,
  onClose,
  material,
  nre,
  overrides,
  onOverrideChange,
  onResetAll,
  discountDefault = 0,
}) => {
  if (!open) return null;

  const nreFallback = NRE_DEFAULTS as unknown as Record<string, number>;
  const materialFallback = MATERIAL_COST_DEFAULTS as unknown as Record<string, number>;
  const defaultValue = (block: Block, key: string): number => {
    if (block === 'flat') return key === 'durationDiscountPct' ? discountDefault : 0;
    if (block === 'nre') {
      return readNested(nre, key) ?? nreFallback[key] ?? DIFFICULTY_MULTIPLIERS[key.split('.').pop() ?? ''] ?? 0;
    }
    return material[key] ?? materialFallback[key] ?? FEE_FALLBACKS[key] ?? 0;
  };

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Cost assumptions">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[#E2E5EA] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold font-heading text-blueprint-blue">Cost assumptions</h3>
            <p className="mt-0.5 text-xs text-heritage-grey">
              From the active configuration. Edit any value to override it for this proposal only.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-heritage-grey hover:text-blueprint-blue" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-medium text-blueprint-blue">{group.title}</h4>
              {group.note && <p className="mt-0.5 text-xs text-heritage-grey">{group.note}</p>}
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                {group.fields.map((field) => {
                  const key = overrideKeyFor(group.block, field.key);
                  return (
                    <FieldRow
                      key={key}
                      field={field}
                      block={group.block}
                      defaultValue={defaultValue(group.block, field.key)}
                      override={overrides[key]}
                      onChange={(value) => onOverrideChange(key, value)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-[#E2E5EA] px-5 py-3">
          <button type="button" onClick={onResetAll} className="text-sm text-text-grey hover:text-blueprint-blue">
            Reset all to defaults
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
